import {memo} from 'react';
import {
  Upload,
  ListChecks,
  Link2,
  TableProperties,
  AlertCircle,
  CheckCircle,
  EyeOff,
  ChevronRight,
  Save,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import {Badge} from '@common-web/components/ui/badge';
import {Button} from '@common-web/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@common-web/components/ui/tooltip';
import clsx from 'clsx';
import {LocalValidationError} from '@client/pages/payroll/utils/validationUtils';

export type ConfigurationStep =
  | 'upload'
  | 'mapping'
  | 'unmapped'
  | 'ignored'
  | 'linking'
  | 'pivot'
  | 'issues';

type StepConfig = {
  id: ConfigurationStep;
  label: string;
  shortLabel: string;
  icon: React.ElementType;
  section: 'core' | 'review' | 'advanced';
  stepNumber?: number;
};

const STEP_CONFIGS: StepConfig[] = [
  {
    id: 'upload',
    label: 'Upload Payroll Files',
    shortLabel: 'Upload',
    icon: Upload,
    section: 'core',
    stepNumber: 1,
  },
  {
    id: 'mapping',
    label: 'Column Mappings',
    shortLabel: 'Mappings',
    icon: ListChecks,
    section: 'core',
    stepNumber: 2,
  },
  {
    id: 'linking',
    label: 'File Linking',
    shortLabel: 'Linking',
    icon: Link2,
    section: 'core',
    stepNumber: 3,
  },
  {
    id: 'issues',
    label: 'Configuration Issues',
    shortLabel: 'Issues',
    icon: AlertTriangle,
    section: 'review',
  },
  {
    id: 'unmapped',
    label: 'Unmapped Columns',
    shortLabel: 'Unmapped',
    icon: AlertCircle,
    section: 'review',
  },
  {id: 'ignored', label: 'Ignored Columns', shortLabel: 'Ignored', icon: EyeOff, section: 'review'},
  {
    id: 'pivot',
    label: 'Deduction Code Pivot',
    shortLabel: 'Pivot',
    icon: TableProperties,
    section: 'advanced',
  },
];

export type StepCompletionStatus = {
  upload: boolean;
  mapping: boolean;
  linking: boolean;
  unmapped: 'hidden' | 'warning' | 'ok';
  ignored: 'hidden' | 'info';
  pivot: 'hidden' | 'configured' | 'available';
};

export const ConfigurationSidebar = memo(
  ({
    activeStep,
    onStepChange,
    completionStatus,
    unmappedCount,
    ignoredCount,
    validationErrors,
    mappedCount,
    totalFields,
    filesExtracted,
    onSaveConfiguration,
    isSaving,
  }: {
    activeStep: ConfigurationStep;
    onStepChange: (step: ConfigurationStep) => void;
    completionStatus: StepCompletionStatus;
    unmappedCount: number;
    ignoredCount: number;
    validationErrors: LocalValidationError[];
    mappedCount: number;
    totalFields: number;
    filesExtracted: boolean;
    onSaveConfiguration: () => void;
    isSaving: boolean;
  }) => {
    const coreSteps = STEP_CONFIGS.filter((s) => s.section === 'core');
    const reviewSteps = STEP_CONFIGS.filter((s) => s.section === 'review');
    const advancedSteps = STEP_CONFIGS.filter((s) => s.section === 'advanced');

    const hasIssues = validationErrors.length > 0;
    const showReviewSection =
      hasIssues || completionStatus.unmapped !== 'hidden' || completionStatus.ignored !== 'hidden';
    const showAdvancedSection = completionStatus.pivot !== 'hidden';

    const getStepStatus = (stepId: ConfigurationStep) => {
      switch (stepId) {
        case 'upload':
          return completionStatus.upload ? 'complete' : 'incomplete';
        case 'mapping':
          return completionStatus.mapping ? 'complete' : 'incomplete';
        case 'linking':
          return completionStatus.linking ? 'complete' : 'incomplete';
        case 'issues':
          return 'warning';
        case 'unmapped':
          return completionStatus.unmapped === 'warning' ? 'warning' : 'complete';
        case 'ignored':
          return 'info';
        case 'pivot':
          return completionStatus.pivot === 'configured' ? 'complete' : 'available';
        default:
          return 'incomplete';
      }
    };

    const isStepVisible = (step: StepConfig) => {
      if (step.id === 'issues' && !hasIssues) return false;
      if (step.id === 'unmapped' && completionStatus.unmapped === 'hidden') return false;
      if (step.id === 'ignored' && completionStatus.ignored === 'hidden') return false;
      if (step.id === 'pivot' && completionStatus.pivot === 'hidden') return false;
      return true;
    };

    const getStepBadge = (step: StepConfig) => {
      if (step.id === 'issues' && hasIssues && filesExtracted) return validationErrors.length;
      if (step.id === 'unmapped' && unmappedCount > 0) return unmappedCount;
      if (step.id === 'ignored' && ignoredCount > 0) return ignoredCount;
      if (step.id === 'mapping' && filesExtracted) return `${mappedCount}/${totalFields}`;
      return null;
    };

    const completedCount =
      Number(completionStatus.upload) +
      Number(completionStatus.mapping) +
      Number(completionStatus.linking);

    const renderFullStepItem = (step: StepConfig) => {
      if (!isStepVisible(step)) return null;

      const isActive = activeStep === step.id;
      const status = getStepStatus(step.id);
      const Icon = step.icon;
      const badge = getStepBadge(step);

      return (
        <button
          key={step.id}
          onClick={() => onStepChange(step.id)}
          className={clsx(
            'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all',
            isActive
              ? 'bg-internal-brand-blue text-white shadow-md'
              : 'hover:bg-gray-100 text-gray-700',
          )}
        >
          <div
            className={clsx(
              'relative shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors',
              isActive
                ? 'bg-white/20'
                : status === 'complete'
                  ? 'bg-emerald/10 text-emerald'
                  : status === 'warning'
                    ? 'bg-amber/10 text-amber'
                    : 'bg-gray-100 text-gray-500',
            )}
          >
            <Icon className='h-4 w-4' />
            {status === 'complete' && !isActive && !badge && (
              <span className='absolute -top-1 -right-1 w-4 h-4 bg-emerald rounded-full flex items-center justify-center'>
                <CheckCircle className='h-3 w-3 text-white' />
              </span>
            )}
            {status === 'warning' && !isActive && badge && (
              <span className='absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-amber rounded-full flex items-center justify-center'>
                <span className='text-[10px] font-bold text-white'>{badge}</span>
              </span>
            )}
          </div>
          <div className='flex-1 min-w-0'>
            {step.stepNumber && (
              <span
                className={clsx(
                  'text-xs font-medium block',
                  isActive ? 'text-white/70' : 'text-gray-400',
                )}
              >
                Step {step.stepNumber}
              </span>
            )}
            <div className={clsx('text-sm font-medium', isActive ? 'text-white' : 'text-gray-800')}>
              {step.label}
            </div>
            {step.id === 'mapping' && badge && (
              <Badge
                className={clsx(
                  'text-xs mt-1',
                  isActive
                    ? 'bg-white/20 text-white border-white/30'
                    : mappedCount === totalFields
                      ? 'bg-emerald/10 text-emerald border-emerald/30'
                      : 'bg-gray-100 text-gray-600 border-gray-200',
                )}
              >
                {badge} mapped
              </Badge>
            )}
          </div>
          <ChevronRight
            className={clsx('h-4 w-4 shrink-0', isActive ? 'text-white/70' : 'text-gray-400')}
          />
        </button>
      );
    };

    const renderCompactStepItem = (step: StepConfig) => {
      if (!isStepVisible(step)) return null;

      const isActive = activeStep === step.id;
      const status = getStepStatus(step.id);
      const Icon = step.icon;
      const badge = getStepBadge(step);

      return (
        <Tooltip key={step.id}>
          <TooltipTrigger asChild>
            <button
              onClick={() => onStepChange(step.id)}
              className={clsx(
                'relative w-10 h-10 rounded-lg flex items-center justify-center transition-all',
                isActive
                  ? 'bg-internal-brand-blue text-white shadow-md'
                  : status === 'complete'
                    ? 'bg-emerald/10 text-emerald hover:bg-emerald/20'
                    : status === 'warning'
                      ? 'bg-amber/10 text-amber hover:bg-amber/20'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200',
              )}
            >
              <Icon className='h-5 w-5' />
              {badge ? (
                <span
                  className={clsx(
                    'absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full text-[10px] font-bold flex items-center justify-center px-1',
                    step.id === 'unmapped' || step.id === 'issues'
                      ? 'bg-amber text-white'
                      : step.id === 'mapping' && mappedCount === totalFields
                        ? 'bg-emerald text-white'
                        : 'bg-gray-500 text-white',
                  )}
                >
                  {typeof badge === 'number' ? badge : mappedCount}
                </span>
              ) : status === 'complete' && !isActive ? (
                <span className='absolute -top-1 -right-1 w-4 h-4 bg-emerald rounded-full flex items-center justify-center'>
                  <CheckCircle className='h-3 w-3 text-white' />
                </span>
              ) : null}
            </button>
          </TooltipTrigger>
          <TooltipContent side='right'>
            <p className='font-medium'>{step.label}</p>
            {step.stepNumber && <p className='text-xs text-white/70'>Step {step.stepNumber}</p>}
            {badge && <p className='text-xs'>{badge}</p>}
          </TooltipContent>
        </Tooltip>
      );
    };

    return (
      <TooltipProvider delayDuration={100}>
        <div className='xl:hidden w-16 shrink-0'>
          <div className='sticky top-4 max-h-[calc(100vh-2rem)] overflow-y-auto space-y-3 scrollbar-none [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'>
            <div className='bg-white rounded-xl border border-gray-200 p-2 shadow-sm'>
              <div className='flex flex-col items-center gap-2'>
                {filesExtracted ? (
                  <>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={hasIssues ? () => onStepChange('issues') : undefined}
                          className={clsx(
                            'w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center relative',
                            hasIssues && 'cursor-pointer hover:bg-gray-200',
                          )}
                        >
                          <span className='text-sm font-bold text-gray-700'>
                            {completedCount}/3
                          </span>
                          {hasIssues && (
                            <span className='absolute -top-1 -right-1 w-4 h-4 bg-amber rounded-full flex items-center justify-center'>
                              <AlertCircle className='h-3 w-3 text-white' />
                            </span>
                          )}
                          {!hasIssues && (
                            <span className='absolute -top-1 -right-1 w-4 h-4 bg-emerald rounded-full flex items-center justify-center'>
                              <CheckCircle className='h-3 w-3 text-white' />
                            </span>
                          )}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side='right'>
                        {hasIssues ? (
                          <p className='text-amber'>
                            {validationErrors.length} issue
                            {validationErrors.length !== 1 ? 's' : ''} to resolve
                          </p>
                        ) : (
                          <p>Ready to save</p>
                        )}
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          onClick={onSaveConfiguration}
                          disabled={isSaving || hasIssues}
                          size='icon'
                          className='w-10 h-10 bg-internal-brand-blue hover:bg-internal-brand-blue-dark text-white shadow-sm disabled:opacity-50'
                        >
                          {isSaving ? (
                            <Loader2 className='h-4 w-4 animate-spin' />
                          ) : (
                            <Save className='h-4 w-4' />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side='right'>
                        <p>Save Configuration</p>
                      </TooltipContent>
                    </Tooltip>
                  </>
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className='w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center'>
                        <Upload className='h-5 w-5 text-gray-400' />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side='right'>
                      <p>Upload files to begin</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            </div>
            <div className='bg-white rounded-xl border border-gray-200 p-2 shadow-sm'>
              <div className='flex flex-col items-center gap-2'>
                {coreSteps.map(renderCompactStepItem)}
              </div>
            </div>
            {showReviewSection && (
              <div className='bg-white rounded-xl border border-gray-200 p-2 shadow-sm'>
                <div className='flex flex-col items-center gap-2'>
                  {reviewSteps.map(renderCompactStepItem)}
                </div>
              </div>
            )}
            {showAdvancedSection && (
              <div className='bg-white rounded-xl border border-gray-200 p-2 shadow-sm'>
                <div className='flex flex-col items-center gap-2'>
                  {advancedSteps.map(renderCompactStepItem)}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className='hidden xl:block w-64 shrink-0'>
          <div className='sticky top-4 max-h-[calc(100vh-2rem)] overflow-y-auto space-y-4 scrollbar-none [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'>
            <div className='bg-white rounded-xl border border-gray-200 p-4 shadow-sm'>
              <div className='text-xs font-medium text-gray-500 uppercase tracking-wider mb-2'>
                Configuration Progress
              </div>
              {filesExtracted ? (
                <>
                  <div className='flex items-center gap-2 mb-3'>
                    <div className='flex-1 h-2 bg-gray-100 rounded-full overflow-hidden'>
                      <div
                        className='h-full bg-emerald rounded-full transition-all duration-300'
                        style={{width: `${(completedCount / 3) * 100}%`}}
                      />
                    </div>
                    <span className='text-sm font-medium text-gray-600'>{completedCount}/3</span>
                  </div>
                  {hasIssues && (
                    <button
                      onClick={() => onStepChange('issues')}
                      className='flex items-center gap-2 text-amber text-sm mb-3 hover:underline w-full text-left'
                    >
                      <AlertCircle className='h-4 w-4' />
                      <span>
                        {validationErrors.length} issue{validationErrors.length !== 1 ? 's' : ''} to
                        resolve
                      </span>
                    </button>
                  )}
                  {!hasIssues && (
                    <div className='flex items-center gap-2 text-emerald text-sm mb-3'>
                      <CheckCircle className='h-4 w-4' />
                      <span>Ready to save</span>
                    </div>
                  )}
                  <Button
                    onClick={onSaveConfiguration}
                    disabled={isSaving || hasIssues}
                    className='w-full bg-internal-brand-blue hover:bg-internal-brand-blue-dark text-white font-semibold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    {isSaving ? (
                      <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                    ) : (
                      <Save className='h-4 w-4 mr-2' />
                    )}
                    Save Configuration
                  </Button>
                </>
              ) : (
                <div className='flex items-center gap-3 py-2 text-gray-400'>
                  <Upload className='h-5 w-5' />
                  <span className='text-sm'>Upload files to begin</span>
                </div>
              )}
            </div>
            <div className='bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden'>
              <div className='px-4 py-3 bg-gray-50 border-b border-gray-200'>
                <div className='text-xs font-semibold text-gray-500 uppercase tracking-wider'>
                  Configure
                </div>
              </div>
              <div className='p-2 space-y-1'>{coreSteps.map(renderFullStepItem)}</div>
            </div>
            {showReviewSection && (
              <div className='bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden'>
                <div className='px-4 py-3 bg-gray-50 border-b border-gray-200'>
                  <div className='text-xs font-semibold text-gray-500 uppercase tracking-wider'>
                    Review
                  </div>
                </div>
                <div className='p-2 space-y-1'>{reviewSteps.map(renderFullStepItem)}</div>
              </div>
            )}
            {showAdvancedSection && (
              <div className='bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden'>
                <div className='px-4 py-3 bg-gray-50 border-b border-gray-200'>
                  <div className='text-xs font-semibold text-gray-500 uppercase tracking-wider'>
                    Advanced
                  </div>
                </div>
                <div className='p-2 space-y-1'>{advancedSteps.map(renderFullStepItem)}</div>
              </div>
            )}
          </div>
        </div>
      </TooltipProvider>
    );
  },
);
